import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { getReporteModelos } from '../lib/api';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, X, Download, BarChart3, Package, Factory } from 'lucide-react';

export default function ReportesPage() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getReporteModelos();
            setData(res.data);
        } catch (error) {
            toast.error('Error al cargar el reporte');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const filtered = useMemo(() => {
        if (!search) return data;
        const s = search.toLowerCase();
        return data.filter(r =>
            r.modelo.toLowerCase().includes(s) ||
            r.marca.toLowerCase().includes(s) ||
            r.tipo.toLowerCase().includes(s) ||
            r.entalle.toLowerCase().includes(s) ||
            r.tela.toLowerCase().includes(s) ||
            r.clasificacion_tela.toLowerCase().includes(s) ||
            r.hilo.toLowerCase().includes(s)
        );
    }, [data, search]);

    const totals = useMemo(() => ({
        registros: filtered.reduce((sum, r) => sum + r.cantidad_registros, 0),
        prendas: filtered.reduce((sum, r) => sum + r.cantidad_prendas, 0),
    }), [filtered]);

    const handleExportCSV = () => {
        const headers = ['Modelo', 'Marca', 'Tipo', 'Entalle', 'Tela', 'Clasif. Tela', 'Hilo', 'Registros', 'Prendas'];
        const rows = filtered.map(r => [r.modelo, r.marca, r.tipo, r.entalle, r.tela, r.clasificacion_tela, r.hilo, r.cantidad_registros, r.cantidad_prendas]);
        const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'reporte_modelos.csv';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('CSV descargado');
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="reportes-page">
            <div>
                <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Reportes de Modelos</h1>
                <p className="text-slate-500 text-sm mt-1">Resumen de modelos con datos de produccion</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg"><Package className="h-5 w-5 text-blue-600" /></div>
                    <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Modelos</p>
                        <p className="text-2xl font-bold text-slate-800">{filtered.length}</p>
                    </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-3">
                    <div className="bg-emerald-100 p-2 rounded-lg"><BarChart3 className="h-5 w-5 text-emerald-600" /></div>
                    <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Registros Produccion</p>
                        <p className="text-2xl font-bold text-slate-800">{totals.registros}</p>
                    </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-3">
                    <div className="bg-amber-100 p-2 rounded-lg"><Factory className="h-5 w-5 text-amber-600" /></div>
                    <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Total Prendas</p>
                        <p className="text-2xl font-bold text-slate-800">{totals.prendas.toLocaleString()}</p>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="relative w-full sm:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Buscar por modelo, marca, tipo, entalle, tela, hilo..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 h-10 bg-white"
                        data-testid="reportes-search"
                    />
                    {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>}
                </div>
                <Button onClick={handleExportCSV} variant="outline" className="bg-white" data-testid="export-csv-btn">
                    <Download className="h-4 w-4 mr-2" />Exportar CSV
                </Button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-3 px-4">Modelo</TableHead>
                                <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-3 px-4">Marca</TableHead>
                                <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-3 px-4">Tipo</TableHead>
                                <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-3 px-4">Entalle</TableHead>
                                <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-3 px-4">Tela</TableHead>
                                <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-3 px-4">Clasif. Tela</TableHead>
                                <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-3 px-4">Hilo</TableHead>
                                <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-3 px-4 text-center">Registros</TableHead>
                                <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-3 px-4 text-center">Prendas</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Cargando reporte...</TableCell></TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">No hay datos para mostrar</TableCell></TableRow>
                            ) : (
                                <>
                                    {filtered.map((row) => (
                                        <TableRow key={row.id} className="border-b border-slate-100 hover:bg-slate-50">
                                            <TableCell className="py-3 px-4 text-sm font-medium text-slate-800">{row.modelo}</TableCell>
                                            <TableCell className="py-3 px-4 text-sm text-slate-700">{row.marca || '-'}</TableCell>
                                            <TableCell className="py-3 px-4 text-sm text-slate-700">{row.tipo || '-'}</TableCell>
                                            <TableCell className="py-3 px-4 text-sm text-slate-700">{row.entalle || '-'}</TableCell>
                                            <TableCell className="py-3 px-4 text-sm text-slate-700">{row.tela || '-'}</TableCell>
                                            <TableCell className="py-3 px-4 text-sm text-slate-700">
                                                {row.clasificacion_tela ? <Badge variant="outline" className="font-normal">{row.clasificacion_tela}</Badge> : '-'}
                                            </TableCell>
                                            <TableCell className="py-3 px-4 text-sm text-slate-700">{row.hilo || '-'}</TableCell>
                                            <TableCell className="py-3 px-4 text-sm text-center">
                                                <Badge className={row.cantidad_registros > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                                                    {row.cantidad_registros}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="py-3 px-4 text-sm text-center font-medium text-slate-800">
                                                {row.cantidad_prendas > 0 ? row.cantidad_prendas.toLocaleString() : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {/* Totals row */}
                                    <TableRow className="bg-slate-50 font-semibold border-t-2 border-slate-300">
                                        <TableCell colSpan={7} className="py-3 px-4 text-sm text-slate-800 text-right">TOTALES</TableCell>
                                        <TableCell className="py-3 px-4 text-sm text-center text-slate-800">{totals.registros}</TableCell>
                                        <TableCell className="py-3 px-4 text-sm text-center text-slate-800">{totals.prendas.toLocaleString()}</TableCell>
                                    </TableRow>
                                </>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
}
